// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

/**
 * STT providers — Cartesia ink-whisper + Deepgram nova-3.
 *
 * Both connect via WebSocket, send PCM s16le audio from InputEngine,
 * receive transcript events. Same interface: connect, sendAudio, disconnect.
 */

const CARTESIA_STT_WS = 'wss://api.cartesia.ai/stt/websocket';
const CARTESIA_STT_MODEL = 'ink-whisper';
const STT_SAMPLE_RATE = 16000;
const STT_ENCODING = 'pcm_s16le';

export function createCartesiaSTT() {
  let ws = null;

  return {
    /**
     * Connect to STT WebSocket.
     * @param {string} token - Short-lived Cartesia token
     * @param {string} [language] - Language code (e.g. 'it', 'en')
     * @param {function} onTranscript - Callback: ({ text, isFinal }) => void
     * @param {function} [onClose] - Called once if the socket closes unexpectedly while live
     * @returns {Promise<boolean>}
     */
    async connect(token, language, onTranscript, onClose) {
      this.disconnect();

      const params = new URLSearchParams({
        model: CARTESIA_STT_MODEL,
        encoding: STT_ENCODING,
        sample_rate: String(STT_SAMPLE_RATE),
        api_key: token,
        max_silence_duration_secs: '4',
        min_volume: '0.1',
        cartesia_version: '2025-04-16',
      });
      if (language) params.set('language', language);

      let live = false;
      let socket;

      return new Promise((resolve) => {
        socket = ws = new WebSocket(`${CARTESIA_STT_WS}?${params}`);

        socket.onopen = () => { live = true; resolve(true); };

        socket.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            console.log('[STT] msg:', data.type, 'is_final:', data.is_final, 'text:', data.text?.slice(0, 40));
            if (data.type === 'transcript') {
              onTranscript({
                text: data.text || '',
                isFinal: !!data.is_final,
              });
            }
          } catch (_) {}
        };

        socket.onerror = () => {
          if (!live) { this.disconnect(); resolve(false); }
        };

        socket.onclose = () => {
          const isCurrent = ws === socket;
          if (isCurrent) ws = null;
          if (isCurrent && live) { live = false; onClose && onClose(); }
        };
      });
    },

    /** Send PCM audio chunk to STT. */
    sendAudio(int16) {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(int16.buffer);
      }
    },

    /** Signal end of audio stream. */
    sendDone() {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send('done'); } catch (_) {}
      }
    },

    disconnect() {
      if (ws) {
        this.sendDone();
        ws.close();
        ws = null;
      }
    },

    get connected() { return ws && ws.readyState === WebSocket.OPEN; },
  };
}


// ═══════════════════════════════════════════
//  DEEPGRAM (nova-3)
// ═══════════════════════════════════════════

const DEEPGRAM_WS = 'wss://api.deepgram.com/v1/listen';
const DEEPGRAM_UTTERANCE_END_MS = '1000';

export function createDeepgramSTT() {
  let ws = null;

  return {
    /**
     * Connect to Deepgram STT WebSocket.
     * @param {string} token - Deepgram API key
     * @param {string} [language] - Language code (e.g. 'it', 'en')
     * @param {function} onTranscript - Callback: ({ text, isFinal }) => void
     * @param {function} [onClose] - Called once if the socket closes unexpectedly while live
     * @returns {Promise<boolean>}
     */
    async connect(token, language, onTranscript, onClose) {
      this.disconnect();
      let accumulated = '';  // Accumulate finalized segments

      const params = new URLSearchParams({
        model: 'nova-3',
        encoding: 'linear16',
        sample_rate: '16000',
        channels: '1',
        interim_results: 'true',
        utterance_end_ms: DEEPGRAM_UTTERANCE_END_MS,
        punctuate: 'true',
        smart_format: 'true',
        endpointing: '10000',
        vad_events: 'true',
      });
      if (language) params.set('language', language);

      let live = false;
      let socket;

      return new Promise((resolve) => {
        socket = ws = new WebSocket(`${DEEPGRAM_WS}?${params}`, ['token', token]);

        socket.onopen = () => { live = true; resolve(true); };

        socket.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === 'Results') {
              const alt = data.channel?.alternatives?.[0];
              if (alt?.transcript) {
                if (data.is_final) {
                  // Finalized segment — accumulate
                  accumulated += (accumulated ? ' ' : '') + alt.transcript;
                  console.log('[STT-DG] final segment, accumulated:', accumulated.slice(0, 50));
                  onTranscript({ text: accumulated, isFinal: false });
                } else {
                  // Interim — show accumulated + current partial
                  const display = accumulated ? accumulated + ' ' + alt.transcript : alt.transcript;
                  onTranscript({ text: display, isFinal: false });
                }
              }
            } else if (data.type === 'UtteranceEnd') {
              console.log('[STT-DG] UtteranceEnd, final text:', accumulated.slice(0, 50));
              onTranscript({ text: '', isFinal: true });
              accumulated = '';
            }
          } catch (_) {}
        };

        socket.onerror = () => {
          if (!live) { this.disconnect(); resolve(false); }
        };

        socket.onclose = () => {
          const isCurrent = ws === socket;
          if (isCurrent) ws = null;
          if (isCurrent && live) { live = false; onClose && onClose(); }
        };
      });
    },

    /** Send PCM audio chunk to Deepgram. */
    sendAudio(int16) {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(int16.buffer);
      }
    },

    /** Signal end of audio stream. */
    sendDone() {
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'CloseStream' })); } catch (_) {}
      }
    },

    disconnect() {
      if (ws) {
        this.sendDone();
        ws.close();
        ws = null;
      }
    },

    get connected() { return ws && ws.readyState === WebSocket.OPEN; },
  };
}
